import { useMemo } from 'react';

export type FieldSize = 'sm' | 'md' | 'lg';

/** Props shared by every field-like control (inputs, selects, pickers). */
export interface FieldControlProps {
  id?: string;
  name?: string;
  /** Marks the control as invalid (`aria-invalid` + danger chrome). */
  invalid?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  size?: FieldSize;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

/*
 * Field chrome, tokens v3.
 *
 * Quiet fields: a surface plus one `border-input` hairline that firms up on hover. There is no second
 * border, no inner shadow and no gradient. Focus is the two-ring treatment (`focus-ring` = 2px gap + 2px
 * turquoise) and the hairline turns accent; invalid swaps the hairline to danger; every state transition
 * runs on `--zo-motion-fast`. Radius is the 6px control radius (`rounded-md`).
 */

/** Chrome for a bare native control (`<input>`, `<textarea>`) that owns its own edge. */
export const inputBase =
  'zo-input block w-full min-w-0 rounded-md border border-border-input bg-surface-2 text-fg placeholder:text-fg-subtle transition-colors motion-fast hover:border-border-input-hover focus-ring focus-visible:border-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-input read-only:bg-readonly read-only:hover:border-border-input';

export const inputInvalid = 'border-danger hover:border-danger focus-visible:border-danger focus-visible:outline-danger';

export const inputSizes: Record<FieldSize, string> = {
  sm: 'h-control-sm px-2.5 text-sm',
  md: 'h-control px-control-x text-table',
  lg: 'h-control-lg px-3.5 text-base',
};

/**
 * Chrome for a composite field: a frame hosting adornments, chips and a transparent inner control.
 * State is driven by `data-invalid`, `data-disabled` and `data-readonly` attributes.
 */
export const fieldFrame =
  'zo-field group/field relative flex w-full min-w-0 items-center gap-1.5 rounded-md border border-border-input bg-surface-2 text-fg transition-colors motion-fast hover:border-border-input-hover focus-ring-within has-[:focus-visible]:border-accent data-invalid:border-danger data-invalid:hover:border-danger data-disabled:cursor-not-allowed data-disabled:opacity-50 data-disabled:hover:border-border-input data-readonly:bg-readonly data-readonly:hover:border-border-input';

export const fieldFrameSizes: Record<FieldSize, string> = {
  sm: 'h-control-sm px-2.5 text-sm',
  md: 'h-control px-control-x text-table',
  lg: 'h-control-lg px-3.5 text-base',
};

/** The transparent inner control rendered inside a `fieldFrame`. */
export const fieldInner =
  'h-full w-full min-w-0 flex-1 bg-transparent p-0 text-inherit outline-none placeholder:text-fg-subtle disabled:cursor-not-allowed read-only:cursor-default';

/** Adornment (prefix/suffix) inside a `fieldFrame`. */
export const fieldAdornment = 'zo-field-adornment inline-flex shrink-0 select-none items-center gap-1 text-fg-muted';

/**
 * Small icon-only button that lives inside a `fieldFrame` (clear, stepper, open picker).
 *
 * `min-h-6 min-w-6` is the hit target, and it is not the same thing as the icon.
 *
 * These render at `size-5` — a 20px box around a 14px glyph, which is the right *look* inside a 30px
 * field. It was also the whole target. WCAG 2.2 AA asks for 24×24 (2.5.8), and the spacing exception that
 * would have excused it does not apply here: "clear" and "open the calendar" sit side by side, so a 24px
 * circle on either one lands on the other. The picker buttons are keyboard tab stops too, so this was a
 * 20px target a person with an unsteady hand had to hit to open a date field at all.
 *
 * The minimum grows the box, not the icon: the glyph stays 14px and the hover chip becomes 24px, which is
 * what the affordance should have been anyway.
 */
export const fieldButton =
  'zo-field-button inline-flex min-h-6 min-w-6 shrink-0 cursor-pointer items-center justify-center rounded-sm text-fg-muted transition-colors motion-fast hover:bg-hover hover:text-fg focus-ring disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Trigger for a control that opens a floating panel (Select, MultiSelect, DatePicker, TimePicker).
 * A real button — never a native `<select>` — so the chrome is ours end to end.
 */
export const triggerBase =
  'zo-trigger group/trigger relative flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md border border-border-input bg-surface-2 text-start text-fg transition-colors motion-fast hover:border-border-input-hover focus-ring aria-expanded:border-accent data-invalid:border-danger data-invalid:hover:border-danger disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-input data-readonly:pointer-events-none data-readonly:bg-readonly';

export const triggerSizes = fieldFrameSizes;

/**
 * Bare trigger for a listbox that lives *inside* another field's frame — today the unit-of-measure picker
 * inside `QuantityInput`. It borrows the host frame's height and edge instead of drawing a second one, so
 * the composite reads as one control rather than a box inside a box.
 */
export const triggerInline =
  'zo-trigger zo-trigger-inline group/trigger flex h-full shrink-0 cursor-pointer items-center gap-0.5 rounded-sm px-1 font-strong uppercase tracking-caps text-fg-muted transition-colors motion-fast hover:bg-hover hover:text-fg focus-ring data-readonly:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50';

export const triggerInlineSizes: Record<FieldSize, string> = { sm: 'text-xs', md: 'text-sm', lg: 'text-sm' };

/** Content wrapper inside a floating listbox / calendar panel (the panel itself is `<FloatingPanel>`). */
export const popupPanel = 'zo-popup min-w-40 overflow-hidden';

/** Scroll container for a listbox inside a popup panel. */
export const popupList = 'm-0 min-h-0 flex-1 list-none overflow-y-auto p-1 scrollbar-thin overscroll-contain';

/**
 * Listbox option row. `data-active` is the keyboard highlight, `aria-selected` the chosen value.
 * The active row uses a surface step; selection is marked by the accent checkmark and label weight,
 * so a keyboard highlight and a selected value never fight for the same signal.
 */
export const optionBase =
  'zo-option relative flex cursor-pointer select-none items-center gap-2.5 rounded-sm px-2 py-1.5 text-table text-fg outline-none transition-colors motion-fast data-active:bg-hover aria-selected:font-strong aria-disabled:cursor-not-allowed aria-disabled:opacity-40';

/** Sticky group heading inside a listbox. */
export const optionGroupLabel =
  'zo-option-group sticky top-0 z-10 bg-surface-2 px-2 pb-1 pt-2 type-overline text-fg-subtle';

/** Muted single-line message row inside a popup (empty, loading, error, hints). */
export const popupMessage = 'flex items-center gap-2 px-3 py-2.5 text-sm text-fg-muted';

/** Separator inside a popup: a hairline, used only between genuinely different kinds of rows. */
export const popupSeparator = 'my-1 h-px bg-rule';

export interface FieldA11yInput {
  invalid?: boolean;
  /** Extra ids to append to `aria-describedby` (help text, error message, counters). */
  describedBy?: Array<string | undefined | false | null> | string;
  'aria-describedby'?: string;
}

export interface FieldA11yAttrs {
  'aria-invalid'?: true;
  'aria-describedby'?: string;
}

/** Derives `aria-invalid` / `aria-describedby` from field props so every control exposes them consistently. */
export function useFieldA11y(input: FieldA11yInput): FieldA11yAttrs {
  const { invalid, describedBy } = input;
  const explicit = input['aria-describedby'];
  return useMemo(() => {
    const ids = [explicit, ...(Array.isArray(describedBy) ? describedBy : [describedBy])].filter(
      (v): v is string => typeof v === 'string' && v.length > 0,
    );
    const attrs: FieldA11yAttrs = {};
    if (invalid) attrs['aria-invalid'] = true;
    if (ids.length) attrs['aria-describedby'] = ids.join(' ');
    return attrs;
  }, [invalid, describedBy, explicit]);
}
